/**
 * WebRTC (WHEP) 拉流播放器：向 MediaMTX 的 /whep 端点 POST SDP offer，
 * 收 answer 完成协商，播放机器人 WHIP 推上来的画面，
 * 并按 robot/{id}/detections（归一化框）在视频上叠加检测框。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { buildWhepUrl, useMqtt, useTopic } from "./mqtt";

type PlayState = "idle" | "connecting" | "playing" | "error";

interface Det {
  track_id?: number;
  label: string;
  conf: number;
  box: { x1: number; y1: number; x2: number; y2: number };
}

function waitIceComplete(pc: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise(resolve => {
    const timer = setTimeout(done, timeoutMs);
    function done() {
      clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", onChange);
      resolve();
    }
    function onChange() {
      if (pc.iceGatheringState === "complete") done();
    }
    pc.addEventListener("icegatheringstatechange", onChange);
  });
}

export function WhepPlayer({ robotId }: { robotId: string }) {
  const { settings } = useMqtt();
  // WHEP 地址由连接栏的「地址 + WebRTC 端口」派生
  const url = buildWhepUrl(settings);
  const [state, setState] = useState<PlayState>("idle");
  const [errMsg, setErrMsg] = useState("");
  const [stats, setStats] = useState({ w: 0, h: 0, fps: 0 });

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const sessionRef = useRef<string | null>(null);
  const genRef = useRef(0);
  // 最近一次的检测结果（归一化框）及其到达时间，用于叠加层绘制
  const detsRef = useRef<{ list: Det[]; ts: number }>({ list: [], ts: 0 });

  useTopic(`robot/${robotId}/detections`, (_topic, payload) => {
    try {
      const m = JSON.parse(payload);
      detsRef.current = { list: m.detections ?? [], ts: performance.now() };
    } catch {
      // 非 JSON 忽略
    }
  });

  /** 按当前视频帧尺寸（object-fit: contain）把归一化框映射到画布并绘制 */
  const drawOverlay = useCallback(() => {
    const video = videoRef.current;
    const cv = canvasRef.current;
    if (!video || !cv) return;
    const cw = cv.clientWidth;
    const ch = cv.clientHeight;
    if (!cw || !ch) return;

    const dpr = window.devicePixelRatio || 1;
    if (cv.width !== Math.round(cw * dpr) || cv.height !== Math.round(ch * dpr)) {
      cv.width = Math.round(cw * dpr);
      cv.height = Math.round(ch * dpr);
    }
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;
    // 视频以 contain 方式铺满，计算实际画面区域（含黑边偏移）
    const scale = Math.min(cw / vw, ch / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    const ox = (cw - dw) / 2;
    const oy = (ch - dh) / 2;

    const { list, ts } = detsRef.current;
    if (performance.now() - ts > 1500) return; // 结果过期，避免残留旧框
    for (const d of list) {
      const { x1, y1, x2, y2 } = d.box;
      const x = ox + x1 * dw;
      const y = oy + y1 * dh;
      const w = (x2 - x1) * dw;
      const h = (y2 - y1) * dh;
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, w, h);
      const label = `#${d.track_id ?? "?"} ${d.label} ${Math.round(d.conf * 100)}%`;
      ctx.font = "14px ui-monospace, monospace";
      const tw = ctx.measureText(label).width;
      const ly = Math.max(0, y - 18);
      ctx.fillStyle = "rgba(34,197,94,0.85)";
      ctx.fillRect(x, ly, tw + 8, 18);
      ctx.fillStyle = "#fff";
      ctx.fillText(label, x + 4, ly + 13);
    }
  }, []);

  const stop = useCallback(() => {
    genRef.current++; // 使 FPS 统计回调失效
    if (sessionRef.current) {
      fetch(sessionRef.current, { method: "DELETE" }).catch(() => {});
      sessionRef.current = null;
    }
    pcRef.current?.close();
    pcRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    detsRef.current = { list: [], ts: 0 };
    const cv = canvasRef.current;
    if (cv) cv.getContext("2d")?.clearRect(0, 0, cv.width, cv.height);
    setState("idle");
    setErrMsg("");
    setStats({ w: 0, h: 0, fps: 0 });
  }, []);

  useEffect(() => () => stop(), [stop]);

  const start = useCallback(async () => {
    stop();
    setState("connecting");
    setErrMsg("");
    const gen = genRef.current;
    const pc = new RTCPeerConnection();
    pcRef.current = pc;

    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });
    pc.ontrack = ev => {
      const video = videoRef.current;
      if (!video || !ev.streams[0]) return;
      video.srcObject = ev.streams[0];
      video.play().catch(() => {});
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed") {
        setErrMsg("ICE 连接失败");
        setState("error");
      } else if (pc.connectionState === "disconnected") {
        setErrMsg("连接中断");
        setState("error");
      }
    };

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitIceComplete(pc, 2000);

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: pc.localDescription?.sdp ?? offer.sdp,
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status}${body ? `：${body.slice(0, 120)}` : ""}`);
      }
      const loc = resp.headers.get("Location");
      if (loc) sessionRef.current = new URL(loc, url).toString();
      const answer = await resp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
      setState("playing");
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : String(e));
      setState("error");
      pc.close();
      if (pcRef.current === pc) pcRef.current = null;
    }

    // FPS 统计 + 叠加层重绘（requestVideoFrameCallback，Safari/Chrome 支持）
    const video = videoRef.current;
    if (video && "requestVideoFrameCallback" in video) {
      let n = 0;
      let t0 = performance.now();
      const tick = () => {
        if (genRef.current !== gen) return;
        drawOverlay();
        n++;
        const el = performance.now() - t0;
        if (el >= 1000) {
          setStats(s => ({ ...s, fps: Math.round((n * 1000) / el) }));
          n = 0;
          t0 = performance.now();
        }
        video.requestVideoFrameCallback(tick);
      };
      video.requestVideoFrameCallback(tick);
    } else if (video) {
      const tick = () => {
        if (genRef.current !== gen) return;
        drawOverlay();
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
  }, [url, stop, drawOverlay]);

  const playing = state === "playing";
  const busy = state === "connecting";

  return (
    <section className="card video-card">
      <div className="card-head">
        <h3 className="grow">检测画面 · WebRTC 拉流</h3>
        {(playing || busy) && (
          <span className="tag">
            {stats.w > 0 ? `${stats.w}×${stats.h} · ` : ""}
            {playing ? `${stats.fps}fps` : "协商中"}
          </span>
        )}
      </div>
      <div className="row">
        <span className="whep-url mono grow" title={url}>{url}</span>
        <button
          className={`btn ${playing || busy ? "btn-outline" : "btn-primary"}`}
          disabled={busy}
          onClick={() => (playing || busy ? stop() : start())}
        >
          {playing ? "停止" : busy ? "连接中…" : "播放"}
        </button>
      </div>
      <div className="video-wrap">
        <video ref={videoRef} className="video-el" muted playsInline autoPlay />
        <canvas ref={canvasRef} className="overlay-canvas" />
        {state !== "playing" && (
          <div className="video-placeholder">
            {state === "idle" && (
              <>
                <span className="glyph">▶</span>
                点击「播放」从 MediaMTX 拉流
                <span className="hint">需要机器人正在 WHIP 推流</span>
              </>
            )}
            {state === "connecting" && <>SDP 协商中…</>}
            {state === "error" && <span className="msg-error">{errMsg}</span>}
          </div>
        )}
      </div>
    </section>
  );
}
